import * as styles from "./CountdownTimer.module.scss";

import React, { useCallback, useEffect, useState } from "react";
export interface CountdownTimerProps {
	eventDate: Date;
	// For testing purposes
	currentDate?: Date;
}

export interface CountdownCardProps {
	value: number;
	unit: "Days" | "Hours" | "Minutes" | "Seconds";
}

const CountdownCard: React.FC<CountdownCardProps> = ({ value, unit }) => {
	return (
		<div className={styles.cardWrapper} aria-hidden="true">
			<span className={styles.card}>{value}</span> {unit}
		</div>
	);
};

export const CountdownTimer: React.FC<CountdownTimerProps> = ({
	eventDate,
	currentDate,
}) => {
	const calculateTimeLeft = useCallback(() => {
		const nowInMilliseconds = currentDate?.getTime() || Date.now();
		const eventTimeInMilliseconds = new Date(eventDate).getTime();
		const difference = eventTimeInMilliseconds - nowInMilliseconds;

		if (difference <= 0) {
			return { days: 0, hours: 0, minutes: 0, seconds: 0 };
		}

		const dayInMilliseconds = 1000 * 60 * 60 * 24;
		const hourInMilliseconds = 1000 * 60 * 60;
		const minuteInMilliseconds = 1000 * 60;
		const secondInMilliseconds = 1000;

		const days = Math.floor(difference / dayInMilliseconds);
		const hours = Math.floor(
			(difference % dayInMilliseconds) / hourInMilliseconds,
		);
		const minutes = Math.floor(
			(difference % hourInMilliseconds) / minuteInMilliseconds,
		);
		const seconds = Math.floor(
			(difference % minuteInMilliseconds) / secondInMilliseconds,
		);

		return { days, hours, minutes, seconds };
	}, [eventDate, currentDate]);

	const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

	useEffect(() => {
		let animationFrameId: number;

		const updateCountdown = () => {
			// Use functional state update to compare previous values with new ones.
			setTimeLeft((prev) => {
				const newTimeLeft = calculateTimeLeft();
				// Only update state if any time value has changed.
				if (
					newTimeLeft.days === prev.days &&
					newTimeLeft.hours === prev.hours &&
					newTimeLeft.minutes === prev.minutes &&
					newTimeLeft.seconds === prev.seconds
				) {
					return prev;
				}
				return newTimeLeft;
			});
			animationFrameId = requestAnimationFrame(updateCountdown);
		};

		animationFrameId = requestAnimationFrame(updateCountdown);

		return () => cancelAnimationFrame(animationFrameId);
	}, [calculateTimeLeft]);

	return (
		<div
			className={styles.wrapper}
			aria-label={`Countdown: ${timeLeft.days} days, ${timeLeft.hours} hours, ${timeLeft.minutes} minutes and ${timeLeft.seconds} seconds left to the event`}
			role="timer"
			aria-atomic="true"
		>
			<CountdownCard value={timeLeft.days} unit="Days" />
			<CountdownCard value={timeLeft.hours} unit="Hours" />
			<CountdownCard value={timeLeft.minutes} unit="Minutes" />
			<CountdownCard value={timeLeft.seconds} unit="Seconds" />
		</div>
	);
};

export default CountdownTimer;
